import { FormattedMessage } from 'react-intl'
import { getRouteApi, Link, useLocation } from '@tanstack/react-router'

import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { useMediaQuery } from '@gto/ui/hooks/use-media-query'
import { ArrowRightIcon } from '@gto/ui/icons/outline'

import { sendGaEvent } from '@/gtag'
import { useAuth } from '@/hooks/use-auth'

import type { DealCardProps } from './deal-card'
import { DealDescriptionRow } from './deal-description-row'
import { DealSignupButton } from './deal-signup-button'
import { DescriptionWrapper } from './description-wrapper'
import { EdgeMultiplierCard } from './edge-multiplier-card'
import { SiteLogo } from './site-logo'

const animationName = 'arrowPulse'

const routeApi = getRouteApi('/_layout/_private/app/deals/$room')
export const DealCardContent = ({
  poker_site_local_s3_url,
  poker_site_name,
  text_string_1,
  text_string_2,
  text_string_3,
  text_string_1_exclusive,
  text_string_2_exclusive,
  text_string_3_exclusive,
  edge_multiplier,
  poker_site_slug,
  withSignUp = false,
  deal_id,
  signup_url,
}: DealCardProps) => {
  const navigate = routeApi.useNavigate()
  const { pathname } = useLocation()
  const isHomeRoute = pathname === '/'

  const isMdAndLower = useMediaQuery('down', 'md')
  const { isAuthorized } = useAuth()

  return (
    <div className="flex flex-nowrap items-center justify-between gap-6 px-6 py-4 md:flex-wrap sm:flex-col sm:px-4 sm:py-6">
      {!isMdAndLower && (
        <div className="flex w-auto flex-[0_0_auto] items-center justify-center md:w-full md:flex-[1_0_auto]">
          <SiteLogo
            logo={poker_site_local_s3_url}
            name={poker_site_name}
          />
        </div>
      )}
      {text_string_1 || text_string_2 || text_string_3 ? (
        <DescriptionWrapper visible={!!text_string_1 || !!text_string_2 || !!text_string_3}>
          <DealDescriptionRow
            exclusive={text_string_1_exclusive}
            text={text_string_1}
          />
          <DealDescriptionRow
            exclusive={text_string_2_exclusive}
            text={text_string_2}
          />
          <DealDescriptionRow
            exclusive={text_string_3_exclusive}
            text={text_string_3}
          />
        </DescriptionWrapper>
      ) : null}
      <div
        className={cn(
          'flex w-auto flex-[1_0_auto] sm:w-full',
          isMdAndLower && !text_string_1 && !text_string_2 && !text_string_3
            ? 'justify-center'
            : 'justify-end',
        )}
      >
        <EdgeMultiplierCard
          multiplier={edge_multiplier}
          site={poker_site_slug}
        />
      </div>
      {!isMdAndLower && (
        <ArrowCta
          centered={withSignUp}
          className="mb-2"
        />
      )}
      <div className="flex w-auto flex-col items-center justify-center gap-1 md:w-full md:gap-2 sm:items-stretch">
        {withSignUp && signup_url && typeof signup_url === 'string' && signup_url.length > 0 ? (
          <DealSignupButton
            dealId={deal_id}
            url={signup_url}
          />
        ) : (
          <>
            <Button
              as={Link}
              className="flex-initial sm:flex-[1_1_0]"
              color="secondary"
              size="large"
              variant="text"
              onClick={() => {
                if (isHomeRoute) {
                  sendGaEvent({
                    event: 'home_room_details',
                    deal_id,
                  })
                }
                navigate({
                  to: `/app/deals/$room`,
                  params: { room: poker_site_slug },
                  search: {
                    auth_url: isAuthorized ? undefined : '/signup',
                  },
                  hash: 'info',
                }).catch(console.error)
              }}
            >
              <FormattedMessage id="common.controls.roomDetails" />
            </Button>

            <Button
              as={Link}
              className="flex-initial sm:flex-[1_1_0]"
              color="primary"
              size="large"
              variant="contained"
              onClick={() => {
                if (isHomeRoute) {
                  sendGaEvent({
                    event: 'home_get_deal',
                    deal_id,
                  })
                }
                navigate({
                  to: `/app/deals/$room`,
                  params: { room: poker_site_slug },
                  search: {
                    auth_url: isAuthorized ? undefined : '/signup',
                  },
                }).catch(console.error)
              }}
            >
              <FormattedMessage id="common.controls.getDealNow" />
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

type ArrowCtaProps = {
  centered: boolean
  className?: string
}

const ArrowCta = ({ centered, className }: ArrowCtaProps) => {
  return (
    <div className={cn('text-primary-main', centered ? 'self-center' : 'self-end', className)}>
      <div className={cn(animationName, 'opacity-70')}>
        <ArrowRightIcon
          height={32}
          width={32}
        />
      </div>
    </div>
  )
}
